import { Fragment, useState, FC, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import ClearDataButton from "components/HeroSearchForm/ClearDataButton";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import fr from "date-fns/locale/fr";

registerLocale("fr", fr);
setDefaultLocale("fr");

export interface StayDatesRangeInputProps {
  className?: string;
  onTotalDayChange: (day: Date) => void;
  availableDays: any;
  fullyDays?: any;
  unavailableDays?: any;
  onClick?: () => void;
  value?: string; // Add value prop
}

const StayDatesRangeInput: FC<StayDatesRangeInputProps> = ({
  className = "flex-1",
  onTotalDayChange,
  availableDays,
  fullyDays,
  unavailableDays,
  onClick,
  value, // Destructure value prop
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  // Reset calendar when value changeas
  useEffect(() => {
    setStartDate(null);
    setSelectedDate(null);
  }, [value]);

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    if (selectedDate !== null) {
      onTotalDayChange(selectedDate);
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date !== null) {
      const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      setSelectedDate(date);
      setStartDate(date);
      onTotalDayChange(utcDate);
    }
  };

  function convertDaysToNumbers(daysArray: any) {
    const daysOfWeek = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
    return daysArray.map((dayName: any) => {
      const index = daysOfWeek.indexOf(dayName);
      return index !== -1 ? index : null;
    });
  }

  const availableDayNumbers = convertDaysToNumbers(availableDays);

  const getDaysBetween = (start: any, end: any) => {
    // Validate input dates
    if (!start || !end) return [];

    const startDate = new Date(start);
    const endDate = new Date(end);

    // Check if dates are valid
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.warn("Invalid dates provided to getDaysBetween:", { start, end });
      return [];
    }

    const days = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      days.push(currentDate.toISOString().split("T")[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const getFullyDaysFromUnavailableDays = () => {
    const result: any[] = [];

    // Ensure unavailableDays is an array and has valid data
    if (!Array.isArray(unavailableDays)) {
      console.warn("unavailableDays is not an array:", unavailableDays);
      return [];
    }

    unavailableDays.forEach((day: any) => {
      if (!day) return;

      // Handle different possible data structures
      const startDay = day.startDay || day.date || day.unavailableDate || day;
      const endDay = day.endDay || day.date || day.unavailableDate || day;

      if (startDay && endDay) {
        const daysBetween = getDaysBetween(startDay, endDay);
        result.push(...daysBetween);
      }
    });

    const uniqueResult = Array.from(new Set(result));
    return uniqueResult;
  };

  const fullyDaysFromUnavailableDays = getFullyDaysFromUnavailableDays();

  const excludedays = (date: Date) => {
    const dayOfWeek = date.getDay();

    const today = new Date();
    const isToday =
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();

    if (isToday) {
      return false;
    }

    const isFullyDay = (fullyDay: string) => {
      const fullyDate = new Date(fullyDay);
      return (
        fullyDate.getFullYear() === date.getFullYear() &&
        fullyDate.getMonth() === date.getMonth() &&
        fullyDate.getDate() === date.getDate()
      );
    };

    const isUnavailableDay = (unavailableDay: string) => {
      const unavailableDate = new Date(unavailableDay);
      return (
        unavailableDate.getFullYear() === date.getFullYear() &&
        unavailableDate.getMonth() === date.getMonth() &&
        unavailableDate.getDate() === date.getDate()
      );
    };

    // Exclure les jours fermés, les dates complètement réservées ET les dates indisponibles
    const isExcluded =
      fullyDays.includes(formatDate(date)) ||
      fullyDaysFromUnavailableDays.some(isFullyDay) ||
      unavailableDays.some(isUnavailableDay);

    // Permettre la sélection seulement si c'est un jour ouvert ET disponible
    return !isExcluded && availableDayNumbers.includes(dayOfWeek);
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-black-300 dark:text-neutral-400">
          <CalendarIcon className="inline-flex w-6 h-6 rounded-full opacity-75 animate-bounce lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow text-left">
          <span className="block font-semibold xl:text-lg">
            {startDate?.toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
            }) || "Ajouter le jour souhaité"}
          </span>
          <span className="block mt-1 text-sm font-light leading-none text-neutral-400"></span>
        </div>
      </>
    );
  };

  return (
    <Popover className={`StayDatesRangeInput z-10 relative flex ${className}`}>
      {({ open, close }) => (
        <>
          <Popover.Button
            onClick={() => {
              if (onClick) onClick();
            }}
            className={`flex-1 flex relative p-3 items-center space-x-3 focus:outline-none ${
              open ? "shadow-lg" : ""
            }`}
          >
            {renderInput()}
            {startDate && open && <ClearDataButton onClick={() => onChangeDate([null, null])} />}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute right-0 left-auto z-10 w-screen max-w-sm px-4 mt-3 xl:-right-10 top-full sm:px-0 lg:max-w-3xl">
              <div className="p-8 overflow-hidden bg-white shadow-lg rounded-3xl ring-1 ring-black ring-opacity-5 dark:bg-neutral-800">
                <DatePicker
                  locale={"fr"}
                  onChange={(date) => {
                    handleDateChange(date);
                    close();
                  }}
                  selected={selectedDate}
                  monthsShown={2}
                  minDate={new Date()}
                  showPopperArrow={false}
                  filterDate={excludedays}
                  inline
                  renderCustomHeader={(p) => <DatePickerCustomHeaderTwoMonth {...p} />}
                  renderDayContents={(day, date) => (
                    <DatePickerCustomDay
                      dayOfMonth={day}
                      date={date}
                      unavailableDays={fullyDaysFromUnavailableDays}
                    />
                  )}
                />
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default StayDatesRangeInput;
